import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import './Dashboard.css';

export function Dashboard() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, needsAction: 0, byStage: {} });

  useEffect(() => {
    async function fetchStats() {
      try {
        const response = await fetch('/api/suppliers', {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        const suppliers = data.suppliers;
        const today = new Date();
        const needsAction = suppliers.filter(s => s.nextActionDate && new Date(s.nextActionDate) <= today).length;
        const byStage = {};
        suppliers.forEach(s => { byStage[s.status] = (byStage[s.status] || 0) + 1; });
        setStats({ total: suppliers.length, needsAction, byStage });
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    }
    fetchStats();
  }, [getToken]);

  const STAGES = ['Identified', 'Intro', 'Vetting', 'Pilot', 'Active', 'At Risk', 'Dormant'];
  const STAGE_COLORS = {
    'Identified': '#3c4899', 'Intro': '#1565c0', 'Vetting': '#e65100',
    'Pilot': '#6a1b9a', 'Active': '#2e7d32', 'At Risk': '#b71c1c', 'Dormant': '#616161'
  };

  return (
    <div className="dashboard-container">
      <NavBar />
      <div className="dashboard-content">
        <div className="dashboard-welcome">
          <h2>Good {getGreeting()}.</h2>
          <p>Here's your supplier pipeline overview.</p>
        </div>

        <div className="dashboard-cards">
          <div className="card">
            <div className="card-value">{stats.total}</div>
            <h3>Total Suppliers</h3>
            <p className="card-label">In system</p>
          </div>
          <div className="card card-alert">
            <div className="card-value">{stats.needsAction}</div>
            <h3>Need Attention</h3>
            <p className="card-label">Overdue next action</p>
          </div>
          <div className="card card-active">
            <div className="card-value">{stats.byStage['Active'] || 0}</div>
            <h3>Active</h3>
            <p className="card-label">Core suppliers</p>
          </div>
          <div className="card card-pipeline">
            <div className="card-value">
              {(stats.byStage['Identified'] || 0) + (stats.byStage['Intro'] || 0) + (stats.byStage['Vetting'] || 0) + (stats.byStage['Pilot'] || 0)}
            </div>
            <h3>In Pipeline</h3>
            <p className="card-label">Identified through Pilot</p>
          </div>
        </div>

        {stats.total > 0 && (
          <div className="stage-breakdown">
            <h3>Pipeline Breakdown</h3>
            <div className="stage-bars">
              {STAGES.filter(s => stats.byStage[s] > 0).map(stage => (
                <div key={stage} className="stage-bar-row" onClick={() => navigate(`/suppliers?status=${stage}`)}>
                  <span className="stage-bar-label">{stage}</span>
                  <div className="stage-bar-track">
                    <div
                      className="stage-bar-fill"
                      style={{ width: `${(stats.byStage[stage] / stats.total) * 100}%`, background: STAGE_COLORS[stage] }}
                    />
                  </div>
                  <span className="stage-bar-count">{stats.byStage[stage]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="dashboard-actions">
          <Link to="/suppliers" className="action-button">View All Suppliers</Link>
          <Link to="/suppliers/new" className="action-button primary">+ Add Supplier</Link>
        </div>
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
